/**
 * CRM Multi-Compagnia · Wizard import SIA Modalità A (4 step)
 */
import { useState } from "react";
import axios from "axios";
import { Upload, Check, ArrowLeft, ArrowRight, Download, Loader2, FileText } from "lucide-react";
import {
  api, CRM_MULTI, Btn, Field, Select, Spinner, ErrorBox, toast
} from "@/components/crm-multi/shared";
import { ColumnMapper } from "@/components/crm-multi/sia/ColumnMapper";
import { DryRunReport } from "@/components/crm-multi/sia/DryRunReport";

const TIPI_ARCHIVIO = [
  { id: "compagnie", label: "Compagnie", desc: "Tabella compagnie assicurative" },
  { id: "prodotti", label: "Prodotti", desc: "Catalogo prodotti per compagnia" },
  { id: "clienti", label: "Clienti", desc: "Anagrafica clienti (PF e Aziende)" },
  { id: "polizze", label: "Polizze", desc: "Polizze in corso (richiede clienti e compagnie già importati)" },
  { id: "subagenti", label: "Subagenti", desc: "Collaboratori e provvigioni" },
  { id: "sinistri", label: "Sinistri", desc: "Sinistri aperti e chiusi" },
  { id: "incassi", label: "Incassi", desc: "Pagamenti ricevuti" },
  { id: "scadenze", label: "Scadenze rate", desc: "Rate da incassare" },
];

const REQ_PER_TIPO = {
  compagnie: ["nome", "slug"],
  prodotti: ["company_id", "nome"],
  clienti: [],
  polizze: ["numero_polizza", "customer_id", "company_id", "product_id", "ramo"],
  sinistri: ["numero_sinistro", "policy_id", "customer_id", "company_id", "ramo", "data_sinistro", "data_denuncia"],
  incassi: ["customer_id", "importo_eur", "data_incasso"],
  scadenze: ["policy_id", "numero_rata", "data_scadenza", "importo_eur"],
  subagenti: ["nome", "cognome"],
};

export default function SiaImportWizardPage({ onBack }) {
  const [step, setStep] = useState(1);
  const [tipo, setTipo] = useState("clienti");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // {batch_id, columns, preview, suggested_mapping, total_rows, parse_info}
  const [mapping, setMapping] = useState({});
  const [targetFields, setTargetFields] = useState([]);
  const [saveDefault, setSaveDefault] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);

  // STEP 2: upload
  const doUpload = async () => {
    if (!file) { toast("Seleziona un file", "error"); return; }
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_archivio", tipo);
      const r = await axios.post(`${CRM_MULTI}/sia/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(r.data);
      setMapping(r.data.suggested_mapping || {});
      // fetch target fields
      const tf = await api.get("/sia/meta/target-fields", { tipo_archivio: tipo });
      setTargetFields(tf.target_fields);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setUploading(false); }
  };

  // STEP 3 → 4: save mapping + simulate
  const doSimulate = async () => {
    setSimulating(true); setError(null);
    try {
      await api.post(`/sia/batches/${uploadResult.batch_id}/mapping`,
                      { mapping, save_as_default: saveDefault });
      const r = await api.post(`/sia/batches/${uploadResult.batch_id}/simulate`);
      setDryRun(r);
      setStep(4);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSimulating(false); }
  };

  // STEP 4: import definitivo
  const doImport = async () => {
    if (!window.confirm("Confermi l'IMPORTAZIONE DEFINITIVA? L'azione è irreversibile.")) return;
    setImporting(true); setError(null);
    try {
      const r = await api.post(`/sia/batches/${uploadResult.batch_id}/import`);
      setImportResult(r);
      toast(`Import completato: ${r.summary.rows_created} creati · ${r.summary.rows_updated} aggiornati`, "success");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      toast("Errore import", "error");
    } finally { setImporting(false); }
  };

  const downloadUrl = (suffix) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/multi/sia/batches/${uploadResult?.batch_id}/${suffix}`;

  return (
    <div className="space-y-5 max-w-5xl" data-testid="sia-wizard">
      <header className="flex items-center gap-3">
        {onBack && <Btn variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4" /></Btn>}
        <div>
          <h2 className="text-xl font-semibold">📥 Import Modalità A · File manuali</h2>
          <p className="text-sm text-zinc-500">Wizard a 4 step</p>
        </div>
      </header>

      {/* Step indicator */}
      <div className="flex items-center gap-2" data-testid="sia-wizard-steps">
        {[1, 2, 3, 4].map(n => (
          <div key={n} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              step === n ? "bg-emerald-600 text-white" : step > n ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-400"
            }`}>{step > n ? <Check className="w-3.5 h-3.5" /> : n}</div>
            {n < 4 && <div className={`w-12 h-0.5 ${step > n ? "bg-emerald-300" : "bg-zinc-200"}`} />}
          </div>
        ))}
        <span className="ml-3 text-sm text-zinc-500">
          {["", "Tipo archivio", "Upload file", "Mapping colonne", "Simulazione & Import"][step]}
        </span>
      </div>

      <ErrorBox error={error} />

      {/* STEP 1 */}
      {step === 1 && (
        <div className="space-y-3" data-testid="sia-wizard-step1">
          <h3 className="font-semibold">Seleziona il tipo di archivio da importare</h3>
          <div className="grid md:grid-cols-2 gap-2">
            {TIPI_ARCHIVIO.map(t => (
              <button key={t.id} onClick={() => setTipo(t.id)}
                data-testid={`sia-tipo-${t.id}`}
                className={`text-left p-3 rounded border-2 ${tipo === t.id ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                <p className="font-medium text-sm capitalize">{t.label}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{t.desc}</p>
              </button>
            ))}
          </div>
          <div className="flex justify-end pt-3">
            <Btn variant="primary" onClick={() => setStep(2)} data-testid="sia-wizard-next-1">
              Avanti <ArrowRight className="w-4 h-4" />
            </Btn>
          </div>
        </div>
      )}

      {/* STEP 2: UPLOAD */}
      {step === 2 && (
        <div className="space-y-3" data-testid="sia-wizard-step2">
          <h3 className="font-semibold">Carica il file <span className="text-zinc-500">({tipo})</span></h3>
          <label className="block">
            <div className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center hover:border-emerald-400 cursor-pointer">
              <Upload className="w-10 h-10 mx-auto mb-2 text-zinc-400" />
              <p className="text-sm text-zinc-700">
                {file ? <strong>{file.name}</strong> : "Clicca per selezionare o trascina qui un file CSV / Excel / XML"}
              </p>
              <p className="text-xs text-zinc-500 mt-1">Max 50MB · Encoding auto-detect · Italiano supportato</p>
              <input type="file" accept=".csv,.xlsx,.xml,.txt,.tsv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden" data-testid="sia-wizard-file-input" />
            </div>
          </label>
          <div className="flex justify-between pt-3">
            <Btn variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4" /> Indietro</Btn>
            <Btn variant="primary" onClick={doUpload} disabled={!file || uploading} data-testid="sia-wizard-upload">
              {uploading && <Loader2 className="w-4 h-4 animate-spin" />} Carica e analizza
            </Btn>
          </div>
        </div>
      )}

      {/* STEP 3: MAPPING */}
      {step === 3 && uploadResult && (
        <div className="space-y-3" data-testid="sia-wizard-step3">
          <div className="bg-zinc-50 border rounded p-3 text-xs text-zinc-700 space-y-0.5">
            <p>📄 <strong>{uploadResult.total_rows}</strong> righe rilevate · Formato: <code>{uploadResult.parse_info?.format}</code>
              {uploadResult.parse_info?.encoding_used && <> · Encoding: <code>{uploadResult.parse_info.encoding_used}</code></>}
              {uploadResult.parse_info?.delimiter_used && <> · Delimitatore: <code>{JSON.stringify(uploadResult.parse_info.delimiter_used)}</code></>}
            </p>
          </div>
          <h3 className="font-semibold">Mappa le colonne file → campi CRM</h3>
          <p className="text-xs text-zinc-500">Le caselle <span className="text-emerald-700">verde chiaro</span> sono i suggerimenti automatici. Modifica se necessario. Asterisco <span className="text-rose-500">*</span> = campo obbligatorio.</p>
          <ColumnMapper
            columns={uploadResult.columns}
            targetFields={targetFields}
            mapping={mapping}
            setMapping={setMapping}
            suggested={uploadResult.suggested_mapping}
            requiredFields={REQ_PER_TIPO[tipo] || []}
            previewRows={uploadResult.preview}
          />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)}
                   data-testid="sia-wizard-save-default" />
            Salva questo mapping come default per i futuri import di tipo "{tipo}"
          </label>
          <div className="flex justify-between pt-3">
            <Btn variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4" /> Indietro</Btn>
            <Btn variant="primary" onClick={doSimulate} disabled={simulating} data-testid="sia-wizard-simulate">
              {simulating && <Loader2 className="w-4 h-4 animate-spin" />} ▶ Esegui simulazione
            </Btn>
          </div>
        </div>
      )}

      {/* STEP 4: DRY RUN + IMPORT */}
      {step === 4 && (
        <div className="space-y-4" data-testid="sia-wizard-step4">
          {!importResult ? (
            <>
              <h3 className="font-semibold">Risultato simulazione</h3>
              <DryRunReport report={dryRun} />
              <div className="flex justify-between pt-3 flex-wrap gap-2">
                <Btn variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4" /> Torna al mapping</Btn>
                <Btn variant="primary" onClick={doImport} disabled={importing} data-testid="sia-wizard-import">
                  {importing && <Loader2 className="w-4 h-4 animate-spin" />} ✅ Conferma e Importa Definitivamente
                </Btn>
              </div>
            </>
          ) : (
            <>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
                <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
                  <Check className="w-5 h-5" /> Import completato!
                </h3>
                <p className="text-sm text-emerald-700 mt-1">
                  <strong>{importResult.summary.rows_created}</strong> creati ·
                  <strong> {importResult.summary.rows_updated}</strong> aggiornati ·
                  <strong> {importResult.summary.rows_skipped}</strong> saltati ·
                  <strong> {importResult.summary.rows_errors}</strong> errori
                </p>
              </div>
              <div className="flex gap-2 flex-wrap" data-testid="sia-wizard-downloads">
                <Btn variant="outline" onClick={() => window.open(downloadUrl("report.pdf"), "_blank")}>
                  <FileText className="w-4 h-4" /> Scarica PDF
                </Btn>
                <Btn variant="outline" onClick={() => window.open(downloadUrl("report.xlsx"), "_blank")}>
                  <Download className="w-4 h-4" /> Scarica Excel
                </Btn>
                {importResult.summary.rows_errors > 0 && (
                  <Btn variant="outline" onClick={() => window.open(downloadUrl("errors.csv"), "_blank")}>
                    <Download className="w-4 h-4" /> Scarica errori CSV
                  </Btn>
                )}
                <Btn variant="primary" onClick={onBack}>🏠 Vai al CRM</Btn>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
